import { useAsync, useDeepCompareEffect } from "@react-hookz/web";
import type { ReactElement, ReactNode, RefObject } from "react";
import React, { useEffect, useState } from "react";
import { useSdkContext } from "scmp-sdk/components/contexts";

import CloseIcon from "./icon-close.svg";
import { CloseButton, StyledTooltip } from "./styles";

export type Props = {
  children: ReactElement;
  isTooltipVisible: boolean;
  tooltipRenders?: Array<TooltipRender>;
};

export type TooltipItem = {
  node: ReactNode;
  onChangeUrl?: () => void;
  onClose?: () => void;
};

export type TooltipRender = RefObject<null | Promise<TooltipItem>>;

export const DashboardTooltips: React.FunctionComponent<Props> = ({
  children,
  isTooltipVisible,
  tooltipRenders,
}) => {
  const [state, actions] = useAsync(async () => {
    if (!tooltipRenders) return null;

    const allSettled = await Promise.allSettled(
      tooltipRenders.map(render => {
        if (!render.current) return Promise.resolve();
        return render.current;
      }),
    );
    for (const settled of allSettled) {
      if (settled.status === "rejected" || settled.value === null) continue;
      return settled.value;
    }
  });

  const [hasShown, setHasShown] = useState(false);

  useDeepCompareEffect(() => {
    if (hasShown) return;
    void actions.execute();
  }, [tooltipRenders, hasShown]);

  const isOpen =
    isTooltipVisible && !hasShown && state.status === "success" && !!state.result?.node;
  const item = state.status === "success" ? state.result : undefined;

  const { router } = useSdkContext();

  useEffect(() => {
    const handleRouteChange = () => {
      if (!isOpen || !item) return;

      setHasShown(true);
      item?.onChangeUrl?.();
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [item, router, isOpen]);

  return (
    <StyledTooltip
      arrow
      open={isOpen}
      placement="bottom-end"
      PopperProps={{
        disablePortal: true,
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [16, 0],
            },
          },
        ],
      }}
      title={
        <>
          {item?.node}
          <CloseButton
            aria-label="close"
            disableRipple
            onClick={() => {
              setHasShown(true);
              item?.onClose?.();
            }}
          >
            <CloseIcon />
          </CloseButton>
        </>
      }
    >
      {children}
    </StyledTooltip>
  );
};

DashboardTooltips.displayName = "DashboardTooltips";
